import { atom } from 'recoil';

export enum MainOptionsEnum {
  WorkHistory = "work-history",
  Skills = "skills",
  AboutMe = "about-me",
  Resume = "resume",
  Projects = "projects",
  Handball = "handball",
  Investments = "investments",
  RealEstate = "real-estate",
  GoBack = "go-back",
}

export enum RenderTypeEnum {
  Typing = "typing",
  Append = "append",
  InputOptions = "input-options",
  OpenLink = "open-link",
  TextLink = "text-link",
}

export enum ViewTypeEnum {
  Text = "text",
  Link = "link",
}

export type OpenLinkTarget = "_blank" | "_self";

/**
 * Render Input Section
 */
type ValidationResult = {
  valid: true;
} | {
  valid: false;
  reason: string;
};

type OptionType<T = string> = {
  label: string;
  value: T;
};

type OptionInputType<T = string> = {
  input: T | null;
  options: OptionType<T>[];
  disabled: boolean;
};

type TextInputType = {
  input: string | null;
  validation: (input: string) => ValidationResult;
  disabled: boolean;
};

type NumberInputType = {
  input: string | null;
  validation: (input: number) => ValidationResult;
  disabled: boolean;
};

type InputType<T = string> = OptionInputType<T> | TextInputType | NumberInputType;

/**
 * Render Output Section
 */

export type TypingOutputType = {
  type: RenderTypeEnum.Typing;
  text: string;
};

export type AppendOutputType = {
  type: RenderTypeEnum.Append;
  text: string;
};

export type OptionsOutputType<T = string> = {
  type: RenderTypeEnum.InputOptions;
  options: OptionType<T>[];
};

export type OpenLinkOutputType = {
  type: RenderTypeEnum.OpenLink;
  url: string;
  target?: OpenLinkTarget;
};

export type TextLinkOutputType = {
  type: RenderTypeEnum.TextLink;
  text: string;
  url: string;
};

export type OutputType<T = String> = TypingOutputType
  | AppendOutputType
  | OptionsOutputType<T>
  | OpenLinkOutputType
  | TextLinkOutputType;

/**
 * View Output Section
 */

export type TextViewType = {
  type: ViewTypeEnum.Text;
  text: string;
};

export type LinkViewType = {
  type: ViewTypeEnum.Link;
  text: string;
  url: string;
};

export type ViewType = TextViewType | LinkViewType;

type ConsoleType = {
  // A queue for the rendering of text to the view.
  rendering: OutputType[];

  // Current console view. The text has been rendered already.
  view: ViewType[];

  // Time to delay rendering of typinging in ms
  renderDelay: number;

  // Time to delay appending text to view
  appendDelay: number;

  // User input section on the console screen
  input: InputType;
};

export const initialConsoleRendering: OutputType<MainOptionsEnum>[] = [
  { type: RenderTypeEnum.Typing, text: "Welcome to calvinlee.dev, how can I help you?" },
  { type: RenderTypeEnum.Append, text: " 1. About Me" },
  { type: RenderTypeEnum.Append, text: " 2. Skills" },
  { type: RenderTypeEnum.Append, text: " 3. Resume" },
  { type: RenderTypeEnum.Append, text: " 4. Projects" },
];

export const initialConsoleOptions: OptionsOutputType<MainOptionsEnum> = {
  type: RenderTypeEnum.InputOptions,
  options: [
    { label: "About Me", value: MainOptionsEnum.AboutMe },
    { label: "Skills", value: MainOptionsEnum.Skills },
    { label: "Resume", value: MainOptionsEnum.Resume },
    { label: "Projects", value: MainOptionsEnum.Projects },
  ],
};

export const initialConsoleView: ViewType[] = [
  { type: ViewTypeEnum.Text, text: "Welcome to calvinlee.dev, how can I help you?" },
  { type: ViewTypeEnum.Text, text: " 1. About Me" },
  { type: ViewTypeEnum.Text, text: " 2. Skills" },
  { type: ViewTypeEnum.Text, text: " 3. Resume" },
  { type: ViewTypeEnum.Text, text: " 4. Projects" },
];

export const initialConsoleQueue: OutputType<MainOptionsEnum>[] = [
  ...initialConsoleRendering,
  {
    ...initialConsoleOptions,
  },
];

export const consoleState = atom<ConsoleType>({
  key: 'consoleState',
  default: {
    rendering: initialConsoleQueue,
    renderDelay: 25,
    appendDelay: 500,
    view: [],
    input: {
      input: null,
      options: [],
      disabled: true,
    },
  },
});
